import { useEffect, useState } from "react";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api from "../lib/api";
import Directory from "./hr/Directory";
import Leaves from "./hr/Leaves";
import TeamApprovals from "./hr/TeamApprovals";
import Payroll from "./hr/Payroll";
import { fmt$ } from "./hr/utils";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Directory", "Leaves", "Team Approvals", "Payroll", "Reports"];

/**
 * HR page — thin shell that owns the top KPI bar, the tab switch and
 * shared data loading (summary, employees, leaves, payrolls). Each tab
 * body lives in its own file under /pages/hr for testability.
 */
export default function HRPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Directory");
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payrolls, setPayrolls] = useState([]);

  const refresh = async () => {
    const [s, e, l, p] = await Promise.all([
      api.get("/hr/summary").then((r) => r.data),
      api.get("/hr/employees").then((r) => r.data),
      api.get("/hr/leaves").then((r) => r.data),
      api.get("/hr/payroll").then((r) => r.data),
    ]);
    setSummary(s); setEmployees(e); setLeaves(l); setPayrolls(p);
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="hr-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 02 · HUMAN RESOURCES</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">People.</h1>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-zinc-200 mb-6">
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Total</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.total_employees}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Active</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.active_employees}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Monthly Payroll</div>
            <div className="font-display font-black text-2xl tnum mt-1">{fmt$(summary.monthly_payroll)}</div>
          </div>
          <div className="px-6 py-4">
            <div className="label-sm text-zinc-500">Pending Leaves</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.pending_leaves}</div>
          </div>
        </div>
      )}

      <div className="flex border-b border-zinc-950">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`hr-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950">
        {tab === "Directory" && <Directory employees={employees} onChange={refresh} />}
        {tab === "Leaves" && <Leaves leaves={leaves} employees={employees} onChange={refresh} />}
        {tab === "Team Approvals" && <TeamApprovals onChange={refresh} />}
        {tab === "Payroll" && <Payroll payrolls={payrolls} onChange={refresh} />}
        {tab === "Reports" && <StandardReports module="hr" />}
      </div>
    </div>
  );
}
